import { HomeOutlined } from '@ant-design/icons';
import { Button, Empty, List, Modal, Space, Spin, Typography, message } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { firebaseFunctions } from '../../firebase/firebase';
import { FirebaseCallableFunctions } from '../../firebase/firebase.models';
import { useGetOffersByUid } from '../../firebase/hooks/getOffersByUid.hook';
import { OfferStatusState } from '../../models/offers.model';
import { useOnAddOfferPush } from '../App/hooks/onAddOfferPush.hook';
import { useAuth } from '../Auth/AuthContext';

const { Title, Text } = Typography;

interface SubmitOfferModalProps {
  visible: boolean;
  onClose: () => void;
  listing: {
    id: string;
    location: string;
    minRent?: number;
    maxRent?: number;
    minSurface?: number;
    description?: string;
  } | null;
}

export const SubmitOfferModal: React.FC<SubmitOfferModalProps> = ({ visible, onClose, listing }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation('wanted-listings');
  const [offers, , loading] = useGetOffersByUid(currentUser?.uid || '');
  const onAddOfferPush = useOnAddOfferPush();
  const [submittingOfferId, setSubmittingOfferId] = useState<string | null>(null);

  // Filtruj tylko aktywne oferty
  const activeOffers = useMemo(() => {
    return offers.filter((offer) => offer.offerStatus.state === OfferStatusState.ACTIVE);
  }, [offers]);

  const handleOfferSelect = async (offerId: string) => {
    if (!listing?.id) {
      message.error(t('ERRORS.NO_LISTING_DATA'));
      return;
    }

    setSubmittingOfferId(offerId);

    try {
      const submitOfferToWantedListing = firebaseFunctions.httpsCallable(
        FirebaseCallableFunctions.SUBMIT_OFFER_TO_WANTED_LISTING
      );

      await submitOfferToWantedListing({
        offerId: offerId,
        wantedListingId: listing.id,
      });

      message.success(t('SUCCESS.OFFER_SENT'));
      onClose();
    } catch (error: any) {
      console.error('Error submitting offer:', error);

      // Obsługa specyficznych błędów
      if (error.code === 'already-exists') {
        message.error(t('ERRORS.ALREADY_SENT'));
      } else if (error.code === 'permission-denied') {
        message.error(t('ERRORS.NO_PERMISSION'));
      } else if (error.code === 'not-found') {
        message.error(t('ERRORS.NOT_FOUND'));
      } else {
        message.error(t('ERRORS.SEND_FAILED'));
      }
    } finally {
      setSubmittingOfferId(null);
    }
  };

  const handleAddNewOffer = () => {
    onAddOfferPush();
    onClose();
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4}>{t('SUBMIT_OFFER_MODAL.TITLE')}</Title>
          {listing && (
            <Text type="secondary">
              {t('LABELS.LOCATION')}: {listing.location}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('SUBMIT_OFFER_MODAL.CANCEL_BUTTON')}
        </Button>,
        <Button key="add" type="primary" onClick={handleAddNewOffer}>
          {t('SUBMIT_OFFER_MODAL.ADD_NEW_OFFER_BUTTON')}
        </Button>,
      ]}
      width={600}>
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      ) : activeOffers.length > 0 ? (
        <List
          dataSource={activeOffers}
          renderItem={(offer) => (
            <List.Item
              actions={[
                <Button
                  key="send"
                  type="primary"
                  loading={submittingOfferId === offer.id}
                  disabled={submittingOfferId !== null && submittingOfferId !== offer.id}
                  onClick={() => handleOfferSelect(offer.id)}>
                  {t('SUBMIT_OFFER_MODAL.SEND_OFFER_BUTTON')}
                </Button>,
              ]}>
              <List.Item.Meta
                avatar={<HomeOutlined style={{ fontSize: 24 }} />}
                title={`${offer.address.street}, ${offer.address.city}`}
                description={
                  <Space>
                    <Text>{offer.price} PLN/mies.</Text>
                    <Text>•</Text>
                    <Text>{offer.areaDetails.surface} m²</Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty description={t('SUBMIT_OFFER_MODAL.NO_OFFERS')} className="py-8" />
      )}
    </Modal>
  );
};
